import constants from 'core/constants/Constants';
import dayjs, { Dayjs } from 'dayjs';
import { initialValuesQueryBuilder } from 'pages/rules/RulesDummyData';

interface RuleData {
  rule_start_date: string;
  rule_end_date: string;
  unique_id: string;
  rule_version: string;
  is_latest: boolean;
  output_formula: any;
  output_formula_json: any;
  rule_applicability: any;
  rule_file_path_json: any;
  [key: string]: any;
}

interface TransformedState {
  formValues: {
    rules_date: [Dayjs, Dayjs];
    unique_id: string;
    rule_version_list?: string[];
    rule_version?: string;
    is_latest?: boolean;
    output_formula: any;
    rule_expression: any;
    [key: string]: any;
  };
  inputSourceFileData: {
    file_upload_status: string;
    [key: string]: any;
  };
  previousValues: {
    unique_id: string;
    output_formula: any;
    rule_expression: any;
    [key: string]: any;
  };
}

function safeJsonParse(value: any) {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

export const transformInitialRuleData = (
  ruleData: RuleData[]
): TransformedState | null => {
  if (!ruleData?.length) return null;

  const firstRule = ruleData[0];
  const startDate = dayjs(firstRule.rule_start_date);
  const endDate = dayjs(firstRule.rule_end_date);
  const isSimpleType = constants.payoutTypes
    .map((item) => item?.value)
    .includes(firstRule?.output_formula?.type);

  return {
    formValues: {
      ...firstRule,
      unique_id: firstRule.unique_id,
      rules_date: [startDate, endDate],
      output_formula:
        safeJsonParse(firstRule?.output_formula) || initialValuesQueryBuilder,
      output_formula_json: safeJsonParse(firstRule?.output_formula_json),
      output_formula_mode_type: firstRule?.output_formula_mode_type
        ? firstRule?.output_formula_mode_type
        : 'single',
      output_formula_type: isSimpleType ? 'simple' : 'advanced',
      rule_applicability:
        safeJsonParse(firstRule?.rule_applicability) ||
        initialValuesQueryBuilder,
      rule_applicability_json: safeJsonParse(
        firstRule?.rule_applicability_json
      ),
      rule_file_path_json: safeJsonParse(firstRule?.rule_file_path_json),
      rule_version_list: ruleData.map((item) => item.rule_version),
      rule_version: firstRule?.rule_version,
      is_latest: firstRule?.is_latest,
      rule_expression:
        safeJsonParse(firstRule?.rule_applicability) ||
        initialValuesQueryBuilder,
    },
    inputSourceFileData: {
      ...safeJsonParse(firstRule?.rule_file_path_json),
      file_upload_status: 'true',
    },
    previousValues: {
      ...firstRule,
      unique_id: firstRule.unique_id,
      output_formula_type: isSimpleType ? 'simple' : 'advanced',
      output_formula:
        safeJsonParse(firstRule?.output_formula) || initialValuesQueryBuilder,
      output_formula_json: safeJsonParse(firstRule?.output_formula_json),
      rule_applicability:
        safeJsonParse(firstRule?.rule_applicability) ||
        initialValuesQueryBuilder,
      rule_applicability_json: safeJsonParse(
        firstRule?.rule_applicability_json
      ),
      rule_expression:
        safeJsonParse(firstRule?.rule_applicability) ||
        initialValuesQueryBuilder,
    },
  };
};

export const transformNewVersionRuleData = (
  ruleData: RuleData[],
  newRuleVersion: string
): TransformedState | null => {
  const newRuleData = ruleData.find(
    (item) => item?.rule_version === newRuleVersion
  );

  if (!newRuleData) return null;

  const startDate = dayjs(newRuleData.rule_start_date);
  const endDate = dayjs(newRuleData.rule_end_date);
  const isSimpleType = constants.payoutTypes
    .map((item) => item?.value)
    .includes(newRuleData?.output_formula?.type);

  const isSpecialCase =
    newRuleData?.rule_applicability?.query === '*' &&
    newRuleData?.output_formula?.query === '*';

  return {
    formValues: {
      ...newRuleData,
      unique_id: newRuleData.unique_id,
      rules_date: [startDate, endDate],
      rule_file_path_json: safeJsonParse(newRuleData?.rule_file_path_json),
      output_formula:
        safeJsonParse(newRuleData?.output_formula) || initialValuesQueryBuilder,
      output_formula_json: safeJsonParse(newRuleData?.output_formula_json),
      output_formula_mode_type: newRuleData?.output_formula_mode_type
        ? newRuleData?.output_formula_mode_type
        : 'single',
      output_formula_type: isSimpleType ? 'simple' : 'advanced',
      rule_applicability:
        safeJsonParse(newRuleData?.rule_applicability) ||
        initialValuesQueryBuilder,
      rule_applicability_json: safeJsonParse(
        newRuleData?.rule_applicability_json
      ),
      rule_expression:
        safeJsonParse(newRuleData?.rule_applicability) ||
        initialValuesQueryBuilder,
      ...(isSpecialCase && {
        rule_group_configuration_status: false,
        enter_amount: newRuleData?.output_formula?.value,
      }),
    },
    inputSourceFileData: {
      ...safeJsonParse(newRuleData?.rule_file_path_json),
      file_upload_status: 'true',
    },
    previousValues: {
      ...newRuleData,
      unique_id: newRuleData.unique_id,
      rule_file_path_json: safeJsonParse(newRuleData?.rule_file_path_json),
      output_formula_type: isSimpleType ? 'simple' : 'advanced',
      output_formula:
        safeJsonParse(newRuleData?.output_formula_json) ||
        initialValuesQueryBuilder,
      output_formula_json: safeJsonParse(newRuleData?.output_formula_json),
      rule_applicability:
        safeJsonParse(newRuleData?.rule_applicability) ||
        initialValuesQueryBuilder,
      rule_applicability_json: safeJsonParse(
        newRuleData?.rule_applicability_json
      ),
      rule_expression:
        safeJsonParse(newRuleData?.rule_applicability) ||
        initialValuesQueryBuilder,
      ...(isSpecialCase && {
        rule_group_configuration_status: false,
        enter_amount: newRuleData?.output_formula?.value,
      }),
    },
  };
};
